/**
 * Linha da tabela de auditoria + detalhes expansíveis — extraídos de index.tsx.
 * A linha é operável por teclado (Enter/Espaço), atendendo frontend.md
 * (nada de clique sem foco/teclado).
 */
import {
  actionStyle,
  actorDisplay,
  describeEntity,
  formatPayload,
  ipDaLinha,
  type AuditEntry,
} from '@/domain/auditoria';
import { describeHandoverAudit } from '@/domain/auditoria/handover';
import { camposAlterados, descreverAcao } from '@/domain/auditoria/descrever-acao';
import type { ChecklistItem } from '@/domain/shift-handover/types';
import { ContextoDaAcao, TabelaDeAlteracoes } from './ContextoDaAcao';
import { HandoverDetail } from './HandoverDetail';

export function AuditRow({
  entry,
  expanded,
  catalogById,
  onToggle,
}: {
  entry: AuditEntry;
  expanded: boolean;
  catalogById: Map<string, ChecklistItem>;
  onToggle: () => void;
}) {
  const { module, label } = describeEntity(entry.entity);
  const when = entry.ts.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  // GUPM-273: eventos de plantão são descritos por momento (abertura, passagem,
  // encerramento) em vez de herdarem o rótulo único da tabela. `null` = evento
  // que não é momento do ciclo → cai no detalhamento genérico.
  const handover =
    entry.entity === 'shift_handovers' ? describeHandoverAudit(entry) : null;

  return (
    <>
      <tr
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        className="border-t border-jf-border hover:bg-jf-surface2 cursor-pointer focus:outline-none focus:bg-jf-surface2 focus-visible:ring-1 focus-visible:ring-jf-yellow"
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <td className="px-3 py-2 text-white tabular-nums whitespace-nowrap">{when}</td>
        <td className="px-3 py-2 text-jf-muted">{module}</td>
        <td className="px-3 py-2 text-white">{handover?.label ?? label}</td>
        <td className="px-3 py-2">
          {/* GUPM-335: o verbo (Baixa da TARP, Login…), com o nome técnico no title. */}
          <span title={entry.action} className={`font-bold text-xs ${actionStyle(entry.action)}`}>
            {descreverAcao(entry)}
          </span>
        </td>
        <td className="px-3 py-2 text-white">
          {entry.actorName
            ? entry.actorName
            : <span className="text-jf-dim italic">{actorDisplay(entry)}</span>}
        </td>
        <td className="px-3 py-2 text-jf-muted font-mono text-xs whitespace-nowrap">{ipDaLinha(entry) ?? '—'}</td>
        <td className="px-3 py-2 text-right text-jf-muted" aria-hidden>{expanded ? '▴' : '▾'}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="bg-jf-surface2/40 px-4 py-4 space-y-4">
            <ContextoDaAcao entry={entry} />
            {handover ? (
              <HandoverDetail view={handover} entry={entry} catalogById={catalogById} />
            ) : (
              <DetalheDaMudanca entry={entry} />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/** Edição com estado anterior → antes/depois; o resto → a linha registrada. */
function DetalheDaMudanca({ entry }: { entry: AuditEntry }) {
  const campos = camposAlterados(entry);
  return campos.length > 0 ? <TabelaDeAlteracoes campos={campos} /> : <PayloadDetail entry={entry} />;
}

function PayloadDetail({ entry }: { entry: AuditEntry }) {
  const fields = formatPayload(entry.payload);
  if (fields.length === 0) {
    return <p className="text-xs text-jf-muted italic">Sem payload registrado.</p>;
  }
  return (
    <dl className="grid gap-2 sm:grid-cols-2">
      {fields.map((f) => (
        <div key={f.key} className="rounded-md border border-jf-border bg-jf-surface p-2 text-xs">
          <dt className="font-bold text-jf-dim">{f.key}</dt>
          <dd className="text-white font-mono break-all mt-0.5">{f.value}</dd>
        </div>
      ))}
    </dl>
  );
}
